import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../core/apiClient';
import { Input } from '../../components/ui/input';

// Buscador combinat de Materials i Escandalls (Nodes) — mateix mecanisme que
// NodeForm.tsx (cerca a /simple-materials/search i /nodes en paral·lel,
// dropdown en portal), extret aquí perque es reutilitza també a
// ExpedientDetail.tsx ("Línies del pressupost" de l'Expedient de Taller).

export interface MaterialSearchResult {
  id: string;
  code: string;
  name: string;
  unit: string;
  costPerUnit: number;
  weightKgPerUnit: number;
  elementTypeId?: number | null;
  compositeCodeId?: number | null;
  pricePerMagnitude?: number;
  technicalImageUrl?: string | null;
}

export interface NodeSearchResult {
  id: string;
  code?: string;
  name: string;
  unit: string;
  cachedTotalDirectCost: number;
  cachedTotalWeightKg?: number;
  compositeCodeId?: number | null;
}

interface ElementTypeOption { id: number; code: string; name: string; }

// Mateixa inferència que NodeForm.tsx: naturalesa de la línia a partir del
// Tipus d'elements del material, per codi/nom.
const NATURE_KEYWORDS: { pattern: RegExp; nature: string }[] = [
  { pattern: /subcontract/i, nature: 'MO_SUBCONTRACT' },
  { pattern: /treball|obra|personal/i, nature: 'MO_PROPIA' },
  { pattern: /servei|transport|log[ií]stic/i, nature: 'SERVEIS' },
];
function natureForElementType(et: ElementTypeOption | undefined): string | undefined {
  if (!et) return undefined;
  const text = `${et.code} ${et.name}`;
  return NATURE_KEYWORDS.find(({ pattern }) => pattern.test(text))?.nature;
}

// Cache de mòdul — el catàleg de Tipus d'elements no canvia dins d'una sessió
// i diverses files de la mateixa taula poden fer servir aquest component alhora.
let elementTypesCache: ElementTypeOption[] | null = null;
async function getElementTypes(): Promise<ElementTypeOption[]> {
  if (elementTypesCache) return elementTypesCache;
  try {
    const res = await api.get<{ success: boolean; data: ElementTypeOption[] }>('/simple-materials/element-types');
    elementTypesCache = res.data ?? [];
  } catch {
    elementTypesCache = [];
  }
  return elementTypesCache;
}

interface Props {
  value: string;
  onChangeText: (text: string) => void;
  onSelectMaterial: (mat: MaterialSearchResult, nature: string | undefined) => void;
  onSelectNode: (node: NodeSearchResult) => void;
  excludeNodeId?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  inputStyle?: CSSProperties;
}

export function MaterialNodeSearchInput({
  value, onChangeText, onSelectMaterial, onSelectNode, excludeNodeId, disabled, placeholder, className, inputStyle,
}: Props) {
  const [materialSuggestions, setMaterialSuggestions] = useState<MaterialSearchResult[]>([]);
  const [nodeSuggestions, setNodeSuggestions] = useState<NodeSearchResult[]>([]);
  const [active, setActive] = useState(false);
  const [searching, setSearching] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const search = async (query: string) => {
    if (query.length < 2) {
      setMaterialSuggestions([]);
      setNodeSuggestions([]);
      setActive(false);
      return;
    }
    setActive(true);
    setSearching(true);
    try {
      const [matRes, nodeRes] = await Promise.all([
        api.get<{ success: boolean; data: MaterialSearchResult[] }>(`/simple-materials/search?q=${encodeURIComponent(query)}&limit=10`),
        api.get<{ success: boolean; data: NodeSearchResult[] }>('/nodes', { search: query, limit: 50 } as Record<string, unknown>),
      ]);
      setMaterialSuggestions(matRes.data ?? []);
      setNodeSuggestions((nodeRes.data ?? []).filter((n) => n.id !== excludeNodeId));
    } catch {
      setMaterialSuggestions([]);
      setNodeSuggestions([]);
    } finally {
      setSearching(false);
    }
  };

  const clear = () => {
    setMaterialSuggestions([]);
    setNodeSuggestions([]);
    setActive(false);
    setRect(null);
  };

  const handleSelectMaterial = async (mat: MaterialSearchResult) => {
    const elementTypes = await getElementTypes();
    const et = mat.elementTypeId != null ? elementTypes.find((e) => e.id === mat.elementTypeId) : undefined;
    onSelectMaterial(mat, natureForElementType(et));
    clear();
  };

  const handleSelectNode = (node: NodeSearchResult) => {
    onSelectNode(node);
    clear();
  };

  return (
    <>
      <Input
        ref={inputRef}
        type="text"
        disabled={disabled}
        value={value}
        onChange={(e) => {
          onChangeText(e.target.value);
          setRect(inputRef.current?.getBoundingClientRect() ?? null);
          search(e.target.value);
        }}
        onBlur={() => setTimeout(clear, 200)}
        className={className}
        style={inputStyle}
        placeholder={placeholder || 'Cercar material o escandall...'}
      />
      {active && rect && createPortal(
        <div
          style={{
            position: 'fixed',
            ...(rect.bottom + 220 > window.innerHeight
              ? { bottom: window.innerHeight - rect.top + 2 }
              : { top: rect.bottom + 2 }),
            left: rect.left,
            minWidth: rect.width,
            width: 'max-content',
            maxWidth: 440,
            zIndex: 9999,
          }}
          className="bg-white border rounded-md shadow-xl max-h-52 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {searching && <div className="px-3 py-2 text-muted-foreground text-xs">Cercant...</div>}
          {!searching && materialSuggestions.length === 0 && nodeSuggestions.length === 0 && (
            <div className="px-3 py-2 text-muted-foreground text-xs">Sense resultats</div>
          )}
          {!searching && materialSuggestions.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-slate-50">Materials</div>
              {materialSuggestions.map((mat) => (
                <div
                  key={mat.id}
                  className="px-3 py-2 hover:bg-slate-100 cursor-pointer border-b last:border-b-0"
                  onMouseDown={() => handleSelectMaterial(mat)}
                >
                  <div className="font-medium text-xs whitespace-normal">{mat.name}</div>
                  <div className="text-muted-foreground text-xs whitespace-normal">
                    {mat.code} | {mat.unit} | {Number(mat.pricePerMagnitude ?? mat.costPerUnit ?? 0).toFixed(2)} €/ut
                  </div>
                </div>
              ))}
            </>
          )}
          {!searching && nodeSuggestions.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-slate-50">Escandalls</div>
              {nodeSuggestions.map((node) => (
                <div
                  key={node.id}
                  className="px-3 py-2 hover:bg-slate-100 cursor-pointer border-b last:border-b-0"
                  onMouseDown={() => handleSelectNode(node)}
                >
                  <div className="font-medium text-xs whitespace-normal">{node.name}</div>
                  <div className="text-muted-foreground text-xs">
                    {node.code ? `${node.code} | ` : ''}{node.unit} | Cost directe: {Number(node.cachedTotalDirectCost || 0).toFixed(2)} €
                  </div>
                </div>
              ))}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

// Modal de cerca més gran (mateix patró que el popup "Vincular Escandall" de
// BudgetForm.tsx) — pensat per obrir-se des d'una icona al costat de l'input,
// quan el desplegable petit es queda curt per triar còmodament entre molts
// resultats.

interface ModalProps {
  open: boolean;
  onClose: () => void;
  onSelectMaterial: (mat: MaterialSearchResult, nature: string | undefined) => void;
  onSelectNode: (node: NodeSearchResult) => void;
  excludeNodeId?: string;
  title?: string;
}

export function MaterialNodeSearchModal({ open, onClose, onSelectMaterial, onSelectNode, excludeNodeId, title }: ModalProps) {
  const [query, setQuery] = useState('');
  const [materialResults, setMaterialResults] = useState<MaterialSearchResult[]>([]);
  const [nodeResults, setNodeResults] = useState<NodeSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setMaterialResults([]);
    setNodeResults([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (query.length < 2) {
      setMaterialResults([]);
      setNodeResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    Promise.all([
      api.get<{ success: boolean; data: MaterialSearchResult[] }>(`/simple-materials/search?q=${encodeURIComponent(query)}&limit=30`),
      api.get<{ success: boolean; data: NodeSearchResult[] }>('/nodes', { search: query, limit: 50 } as Record<string, unknown>),
    ]).then(([matRes, nodeRes]) => {
      if (cancelled) return;
      setMaterialResults(matRes.data ?? []);
      setNodeResults((nodeRes.data ?? []).filter((n) => n.id !== excludeNodeId));
    }).catch(() => {
      if (cancelled) return;
      setMaterialResults([]);
      setNodeResults([]);
    }).finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [query, open, excludeNodeId]);

  const handleSelectMaterial = async (mat: MaterialSearchResult) => {
    const elementTypes = await getElementTypes();
    const et = mat.elementTypeId != null ? elementTypes.find((e) => e.id === mat.elementTypeId) : undefined;
    onSelectMaterial(mat, natureForElementType(et));
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-[560px] max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center gap-3">
          <h2 className="font-semibold text-foreground text-sm shrink-0">{title || 'Cercar material o escandall'}</h2>
          <input
            autoFocus
            className="flex-1 px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Cercar per nom o codi..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {searching && <p className="p-4 text-xs text-muted-foreground text-center">Cercant...</p>}
          {!searching && query.length >= 2 && materialResults.length === 0 && nodeResults.length === 0 && (
            <p className="p-4 text-xs text-muted-foreground text-center">Sense resultats</p>
          )}
          {!searching && query.length < 2 && (
            <p className="p-4 text-xs text-muted-foreground text-center">Escriu almenys 2 caràcters per cercar</p>
          )}
          {!searching && materialResults.length > 0 && (
            <>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-slate-50">Materials</div>
              {materialResults.map((mat) => (
                <button
                  key={mat.id}
                  onClick={() => handleSelectMaterial(mat)}
                  className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 text-xs"
                >
                  <span className="font-medium text-foreground">{mat.name}</span>
                  <span className="ml-2 text-muted-foreground text-xs">{mat.code}</span>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {mat.unit} · {Number(mat.pricePerMagnitude ?? mat.costPerUnit ?? 0).toFixed(2)} €/ut
                  </div>
                </button>
              ))}
            </>
          )}
          {!searching && nodeResults.length > 0 && (
            <>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-slate-50">Escandalls</div>
              {nodeResults.map((node) => (
                <button
                  key={node.id}
                  onClick={() => { onSelectNode(node); onClose(); }}
                  className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 text-xs"
                >
                  <span className="font-medium text-foreground">{node.name}</span>
                  {node.code && <span className="ml-2 text-muted-foreground text-xs">{node.code}</span>}
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {node.unit} · Cost directe: {Number(node.cachedTotalDirectCost || 0).toFixed(2)} €
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
        <div className="p-3 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-muted-foreground hover:bg-gray-100 rounded">
            Cancel·lar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
